import { Injectable } from '@angular/core';
import { DeviceDetectorService } from 'ngx-device-detector';
import * as forge from 'node-forge';
import { AppConfigService } from './app-configuration.service';
import { StorageService } from './storage.service';

const DEVICE_PREFFIX_ATTRIBUTE = 'devicePreffix';

@Injectable({ providedIn: 'root' })
export class DeviceManagementService {
  deviceInfo: any;
  deviceId: string;
  publicKey: string;
  devicePreffix: string;
  deviceKeys: any;
  constructor(
    private _storageService: StorageService,
    private _appConfig: AppConfigService,
    private _deviceService: DeviceDetectorService,
  ) {
    this.devicePreffix = this._appConfig.getParameter(DEVICE_PREFFIX_ATTRIBUTE);
  }

  public async createKeyPair() {
    let keyPair;
    let keyPairPem;
    const options = {
      bits: 2048
    };
    await forge.pki.rsa.generateKeyPair(options, (err, keypair) => {
      keyPair = keypair;
      keyPairPem = {
        publicKey: forge.pki.publicKeyToPem(keypair.publicKey),
        privateKey: forge.pki.privateKeyToPem(keypair.privateKey),
      };
    });
    return { keyPairPem, keyPair };
  }

  public async setAppDevice(deviceId, platformKey) {
    this.deviceId = deviceId;
    const { keyPair, keyPairPem } = await this.createKeyPair();
    this.deviceKeys = keyPair;
    this.publicKey = keyPairPem.publicKey;
    this._storageService.setItem(`${this.devicePreffix}-DEVICE-ID`, deviceId);
    this._storageService.setItem(`${this.devicePreffix}-DEVICE-${deviceId}`, { deviceId, platformKey, ...keyPairPem });
  }

  public async loadAppDevice() {
    this.deviceId = await this._storageService?.getItem(`${this.devicePreffix}-DEVICE-ID`);
    if (!this.deviceId) {
      return null;
    }
    this.deviceInfo = {
      deviceId: this.deviceId,
      ...this._deviceService.getDeviceInfo(),
      isMobile: this._deviceService.isMobile(),
      isTablet: this._deviceService.isTablet(),
      isDesktopDevice: this._deviceService.isDesktop(),
    };
    if (!this.deviceKeys) {
      const { privateKey, publicKey, platformKey } = await this._storageService
        .getItem(`${this.devicePreffix}-DEVICE-${this.deviceId}`);
      if (privateKey && publicKey && platformKey) {
        this.deviceKeys = {
          privateKey: forge.pki.privateKeyFromPem(privateKey),
          publicKey: forge.pki.publicKeyFromPem(publicKey),
          platformKey: forge.pki.publicKeyFromPem(platformKey),
        };
        this.publicKey = publicKey;
      }
    }
  }

  public async getAppDevice() {
    if (!this.deviceInfo || !this.deviceKeys) {
      await this.loadAppDevice();
    }
    const devicePublicInfo = {
      deviceId: this.deviceId,
      deviceData: this.deviceInfo,
      publicKey: this.publicKey,
    };
    devicePublicInfo?.deviceData && delete devicePublicInfo.deviceData.deviceId;
    return devicePublicInfo;
  }

  public async getDeviceId() {
    await this.getAppDevice();
    return this.deviceId;
  }

  public async removeDevice() {
    const deviceId = await this._storageService?.getItem(`${this.devicePreffix}-DEVICE-ID`);
    if (deviceId) {
      this._storageService?.removeItem(`${this.devicePreffix}-DEVICE-ID`);
      this._storageService?.removeItem(`${this.devicePreffix}-DEVICE-${deviceId}`);
    }
  }

  public async encryptData(data) {
    if (!this.deviceInfo || !this.deviceKeys) {
      await this.loadAppDevice();
    }
    const { publicKey } = this.deviceKeys;
    return publicKey.encrypt(data);
  }

  public async decryptData(data) {
    if (!this.deviceInfo || !this.deviceKeys) {
      await this.loadAppDevice();
    }
    const { privateKey } = this.deviceKeys;
    const rawData = forge.util.decode64(data);
    return privateKey.decrypt(rawData);
  }

  public async signData(data) {
    if (!this.deviceInfo || !this.deviceKeys) {
      await this.loadAppDevice();
    }
    const { privateKey } = this.deviceKeys;
    const md = forge.md.sha256.create();
    md.update(data);
    const sign = privateKey.sign(md);
    const signedData = forge.util.encode64(sign);
    return signedData;
  }
}
